import React, { useEffect, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowRightToBracket,
  faArrowUpRightFromSquare,
  faBars,
  faBolt,
  faDownload,
  faFlask,
  faMobileScreen,
  faXmark,
} from '@fortawesome/free-solid-svg-icons';
import { API_BASE_URL } from '../../config/api';

interface PublicFirmwareShellProps {
  children: React.ReactNode;
}

interface VersionInfo {
  serverVersion: string;
}

const sidebarLinkClass = ({ isActive }: { isActive: boolean }) =>
  `admin-sidebar-link${isActive ? ' active' : ''}`;

const sidebarSubLinkClass = ({ isActive }: { isActive: boolean }) =>
  `admin-sidebar-link admin-sidebar-sublink${isActive ? ' active' : ''}`;

const PublicFirmwareShell: React.FC<PublicFirmwareShellProps> = ({ children }) => {
  const [versionInfo, setVersionInfo] = useState<VersionInfo | null>(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const showFlasherLinks = location.pathname !== '/releases';

  const closeMobileMenu = () => setIsMobileMenuOpen(false);

  useEffect(() => {
    const fetchVersionInfo = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/version`);
        if (response.ok) {
          setVersionInfo(await response.json());
        }
      } catch (error) {
        console.error('Failed to fetch version info:', error);
      }
    };

    fetchVersionInfo();
  }, []);

  return (
    <div className="admin-app-shell">
      <div className="admin-dashboard-shell">
        <div className={`admin-layout${isMobileMenuOpen ? ' mobile-menu-open' : ''}`}>
          <div className="admin-mobile-topbar">
            <button
              type="button"
              className="admin-mobile-menu-button"
              onClick={() => setIsMobileMenuOpen(true)}
              aria-label="Open navigation menu"
              aria-expanded={isMobileMenuOpen}
            >
              <FontAwesomeIcon icon={faBars} />
            </button>
            <div className="admin-mobile-title">
              <img src="/logo-mark.png" width="32" alt="Fluora" />
              <span>Firmware</span>
            </div>
          </div>

          {isMobileMenuOpen && (
            <button
              type="button"
              className="admin-mobile-menu-backdrop"
              onClick={closeMobileMenu}
              aria-label="Close navigation menu"
            />
          )}

          <aside className={`admin-sidebar${isMobileMenuOpen ? ' open' : ''}`}>
            <div className="admin-sidebar-brand">
              <img src="/logo-mark.png" width="48" alt="Fluora" />
              <div className="admin-sidebar-title">Firmware</div>
              <button
                type="button"
                className="admin-sidebar-close"
                onClick={closeMobileMenu}
                aria-label="Close navigation menu"
              >
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </div>

            <div className="admin-sidebar-user">
              <span className="admin-sidebar-user-label">Public</span>{' '}
              <span className="admin-sidebar-user-name">Firmware Tools</span>
            </div>

            <nav className="admin-sidebar-nav">
              {showFlasherLinks && (
                <NavLink to="/update" className={sidebarLinkClass} onClick={closeMobileMenu}>
                  <FontAwesomeIcon icon={faBolt} fixedWidth />
                  Update
                </NavLink>
              )}
              {showFlasherLinks && (
                <NavLink to="/beta" className={sidebarLinkClass} onClick={closeMobileMenu}>
                  <FontAwesomeIcon icon={faFlask} fixedWidth />
                  Beta
                </NavLink>
              )}

              <div className="admin-sidebar-section">Public Links</div>
              <a href="https://wiki.fluoraplant.com/" target="_blank" rel="noopener noreferrer" className="admin-sidebar-link admin-sidebar-sublink admin-sidebar-external-link" onClick={closeMobileMenu}>
                <FontAwesomeIcon icon={faArrowUpRightFromSquare} fixedWidth />
                Wiki
              </a>
              <NavLink to="/releases" className={sidebarSubLinkClass} onClick={closeMobileMenu}>
                <FontAwesomeIcon icon={faArrowUpRightFromSquare} fixedWidth />
                Releases
              </NavLink>
              <NavLink to="/mobile-app" className={sidebarSubLinkClass} onClick={closeMobileMenu}>
                <FontAwesomeIcon icon={faMobileScreen} fixedWidth />
                Mobile App
              </NavLink>
              <NavLink to="/download" className={sidebarSubLinkClass} onClick={closeMobileMenu}>
                <FontAwesomeIcon icon={faDownload} fixedWidth />
                Firmware Download
              </NavLink>
              <NavLink to="/legacy" className={sidebarSubLinkClass} onClick={closeMobileMenu}>
                <FontAwesomeIcon icon={faDownload} fixedWidth />
                Legacy Download
              </NavLink>
            </nav>

            <div className="admin-sidebar-footer">
              <div className="admin-sidebar-version">
                <span className="admin-sidebar-version-label">Server:</span>{' '}
                <span className="admin-sidebar-version-number">
                  {versionInfo ? `v${versionInfo.serverVersion}` : '...'}
                </span>
              </div>

              <a href="/" className="btn btn-sm admin-sidebar-logout-btn">
                <FontAwesomeIcon icon={faArrowRightToBracket} className="me-2" />
                Login
              </a>
            </div>
          </aside>

          <div className="admin-main">
            <div className="admin-main-inner">
              <div className="admin-main-content">
                {children}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PublicFirmwareShell;
